import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {throttle, kebabCase} from 'lodash';
import {LockClosedIcon} from '@heroicons/react/outline';

import {H2} from '../../components/Markdown';
import MDX from '../../components/MDX';
import FAQSection from '../../components/FAQSection';
import GuideResumesPreview from './GuideResumesPreview';
import DocumentHeader from '../../components/DocumentHeader';
import {Article} from '../../components/LinkedData';
import TableOfContents, {generateTOC} from '../../components/TableOfContents';
import AuthorCard from '../../components/AuthorCard';
import SidebarCard, {CardTypes} from '../../components/SidebarCard';
import SignupBox from '../../components/SignupBox';
import YouTubeVideo from '../../components/YouTubeVideo';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import Link from '../../components/Link';
import {GuideCompanyContext} from '../../components/GuideCompanyContext';
import {QuestionsBatchProvider} from '../../components/QuestionsBatchContext';
import {useAppContext, useAnalytics} from '../../hooks';
import {recordGuideVisit} from '../../api/api';
import {getHeaders} from './util';

const scrollIntoView = (id, offset = 100) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({left: 0, top: el.offsetTop - offset, behavior: 'smooth'});
};

const FALLBACK_VISIBLE_PREVIEW_PARAGRAPHS = 2;

const isParagraphBlock = (block) => {
  const trimmed = block.trim();
  return trimmed
    && !trimmed.startsWith('#')
    && !trimmed.startsWith('<')
    && !trimmed.startsWith('```')
    && !trimmed.startsWith('|')
    && !trimmed.startsWith('- ')
    && !trimmed.startsWith('* ')
    && !/^\d+\./.test(trimmed);
};

const splitAfterFallbackParagraphs = (content) => {
  const blocks = content.split(/\n{2,}/);
  let paragraphCount = 0;
  let visibleEnd = blocks.length;

  for (let i = 0; i < blocks.length; i += 1) {
    if (isParagraphBlock(blocks[i])) paragraphCount += 1;
    if (paragraphCount >= FALLBACK_VISIBLE_PREVIEW_PARAGRAPHS) {
      visibleEnd = i + 1;
      break;
    }
  }

  return {
    visibleContent: blocks.slice(0, visibleEnd).join('\n\n'),
    lockedContent: blocks.slice(visibleEnd).join('\n\n'),
  };
};

const getFirstParagraphBlock = (content) => content
  .split(/\n{2,}/)
  .find(isParagraphBlock) || null;

const splitGuidePreviewContent = (content) => {
  if (!content) return {content: null, lockedPreviewContent: null};

  const h2Matches = [...content.matchAll(/^##\s+.+$/gm)];
  const splitAt = h2Matches[1] ?? null;

  if (!splitAt) {
    if (!h2Matches[0]) {
      const {visibleContent, lockedContent} = splitAfterFallbackParagraphs(content);
      return {
        content: visibleContent,
        lockedPreviewContent: getFirstParagraphBlock(lockedContent),
      };
    }
    // Only one ## — fall back to splitting after it
    const visibleEnd = h2Matches[0].index + h2Matches[0][0].length;
    const hiddenContent = content.slice(visibleEnd).trimStart();
    return {
      content: content.slice(0, visibleEnd),
      lockedPreviewContent: getFirstParagraphBlock(hiddenContent),
    };
  }

  const visibleEnd = splitAt.index + splitAt[0].length;
  const hiddenContent = content.slice(visibleEnd).trimStart();
  return {
    content: content.slice(0, visibleEnd),
    lockedPreviewContent: getFirstParagraphBlock(hiddenContent),
  };
};

const GuideMDX = (props) => (
  <MDX
    className="prose lg:prose-lg prose-indigo"
    allowRaw
    {...props}
  />
);

const GuideUpgradeCTA = ({id, loginHref, onClickUpgrade}) => {
  const {currentUser} = useAppContext();
  return (
    <div
      id={id}
      className="mt-8 flex flex-col items-center rounded-lg border border-gray-200 bg-white px-6 py-8 text-center shadow-sm"
    >
      <div className="rounded-full bg-gray-50 p-3 shadow-sm mb-4">
        <LockClosedIcon className="h-6 w-6 text-gray-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        Unlock this guide and more
      </h3>
      <p className="text-sm text-gray-600 text-center mb-6 max-w-md">
        Members get the complete guide, all prep courses, practice tools, job referrals,
        and everything you need to land your offer.
      </p>
      <div className="flex flex-col items-center gap-3">
        <Button
          buttonType={ButtonTypes.SKEUOMORPHIC}
          href="/upgrade?src=guide"
          onClick={onClickUpgrade}
        >
          Become a member
        </Button>
        {!currentUser && (
          <Link
            href={loginHref}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Already a member? Log in
          </Link>
        )}
      </div>
    </div>
  );
};

const LockedContentPreview = ({children}) => (
  <div className="relative pointer-events-none select-none">
    <div style={{filter: 'blur(4px)'}}>
      {children}
    </div>
    <div className="absolute inset-0 bg-white opacity-20" />
  </div>
);

const LockedSection = ({
  children, lockedContent, loginHref, onClickUpgrade,
}) => (
  <div>
    {children}
    {lockedContent && (
      <LockedContentPreview>
        <GuideMDX>
          {lockedContent}
        </GuideMDX>
      </LockedContentPreview>
    )}
    <GuideUpgradeCTA
      id="guide-upgrade-cta"
      loginHref={loginHref}
      onClickUpgrade={onClickUpgrade}
    />
  </div>
);

const Section = ({title, children}) => (
  <section className="mt-10">
    {title && (
      <span className="prose lg:prose-lg prose-indigo">
        <H2 className="font-bold text-lg lg:text-2xl text-gray-900 mb-4">{title}</H2>
      </span>
    )}
    {children}
  </section>
);

const extractHeadings = (content) => {
  if (!content) return [];
  return [...content.matchAll(/^##\s+(.+)$/gm)].map((m) => {
    const text = m[1].trim();
    return {id: kebabCase(text), text};
  });
};

const GuideSidebarCard = ({guide, tracker}) => {
  if (['swe', 'forward-deployed-engineer'].includes(guide.roleTag)) {
    return (
      <SidebarCard
        cardType={CardTypes.PRIMARY}
        headerText="Interviewed for a FDE role recently?"
        bodyText="Talk to us about your forward-deployed engineer interview experience and get compensated."
        ctaText="Chat now →"
        href="https://call.insightscompany.io/c4e614c3-ce9b-401e-b2b8-b286b9ea6ea4/9f524116-caf4-4813-a205-cfe62a89fd92?src=guide"
        onClick={() => tracker.guideClickCard({type: 'fde-research', tag: guide.roleTag})}
      />
    );
  }

  if (guide.roleTag === 'data-analyst') {
    return (
      <SidebarCard
        cardType={CardTypes.PRIMARY}
        headerText="Practice SQL interviews with peers"
        bodyText="Free daily sessions. Practice real SQL interview questions and get feedback."
        ctaText="Practice now →"
        href="/practice/sql-mock-interviews?src=guide"
        onClick={() => tracker.guideClickCard({type: 'sql-practice', tag: guide.roleTag})}
      />
    );
  }

  return (
    <SidebarCard
      cardType={CardTypes.PRIMARY}
      headerText={`Explore more ${guide.roleName} content`}
      bodyText="Watch mock interviews, review fundamentals, and get insider interview tips."
      ctaText="Explore now →"
      href={`/courses?tag=${guide.roleTag}&src=guide`}
      onClick={() => tracker.guideClickCard({type: 'courses', tag: guide.roleTag})}
    />
  );
};

const GuidePage = ({
  guide, company, preview,
}) => {
  const {
    currentUser,
    hasSubscription,
    hasReferrerAccess,
    loading: appContextLoading,
  } = useAppContext();
  // Phase 5 preview strip: three resumes on the guide itself.
  const showResumesPreview = !preview;
  const {tracker} = useAnalytics();
  const loginHref = `/login?src=guide&returnPath=${encodeURIComponent(`/guides/${guide.slug}`)}`;
  const headers = useMemo(() => getHeaders(guide), [guide]); // deprecated
  const [scrolled, setScrolled] = useState(false); // deprecated
  const [activeHeader, setActiveHeader] = useState(null); // deprecated
  const [toc, setTOC] = useState();
  const headerRef = useRef();
  const bodyRef = useRef();

  const hasAccess = guide.free || appContextLoading || hasSubscription || hasReferrerAccess;
  const previewContent = useMemo(
    () => splitGuidePreviewContent(guide.content),
    [guide.content],
  );
  const renderedContent = hasAccess ? guide.content : previewContent.content;
  const lockedPreviewContent = hasAccess ? null : previewContent.lockedPreviewContent;
  const allSections = useMemo(() => extractHeadings(guide.content), [guide.content]);
  const tocSections = !hasAccess && allSections.length
    ? allSections.map((section, index) => ({...section, locked: index > 0}))
    : toc;
  const tocReady = guide.content ? Array.isArray(tocSections) : true;

  useEffect(() => {
    if (window.location.hash) {
      scrollIntoView(window.location.hash.substring(1));
    }
  }, []);

  useEffect(() => {
    if (!guide) return;

    if (renderedContent) setTOC(generateTOC(bodyRef.current));

    if (!preview) {
      tracker.companyView({guide: guide.name});
      if (currentUser && !appContextLoading && hasAccess) recordGuideVisit(guide.slug);
    }
  }, [guide, renderedContent, appContextLoading]);

  useEffect(() => {
    // Note: Remove effect after migrating content.
    const listener = throttle(() => {
      if (!headerRef.current) return;
      const {top} = headerRef.current.getBoundingClientRect();

      if (!scrolled && top <= 0) {
        setScrolled(true);
      } else if (scrolled && top > 0) {
        setScrolled(false);
      }

      const active = headers.find((header) => {
        const el = document.getElementById(header.slug);
        const section = el ? el.parentElement : null;
        if (section) {
          const {top: sectionTop, bottom} = section.getBoundingClientRect();
          return sectionTop < 120 && bottom > 120; // TODO make constants here...
        }
        return false;
      });

      if (scrolled) {
        if (active && active !== activeHeader) {
          setActiveHeader(active.slug);
        }
      } else {
        setActiveHeader(null);
      }
    }, 50);

    window.addEventListener('scroll', listener);

    return () => window.removeEventListener('scroll', listener);
  });

  const companyName = company?.name || guide.companyTag || '';

  let contributeLink = '/questions/contribute?src=guide';
  if (company) contributeLink += `&company=${company.name}`;
  if (guide.roleTag) contributeLink += `&role=${guide.roleTag}`;

  const description = `
    Prepare for the ${companyName} ${guide.roleName || ''} interview with an inside look at the
    interview process and sample questions. Learn how to get a ${guide.roleName || ''} job
    at ${companyName} with essential tips from past interviewers and hiring managers.
  `;

  return (
    <div className="bg-white resize-iframe">
      {/* Metadata */}
      {/* imgPath={null} so this header doesn't clobber GuideLayout's
          dynamic og:image with the static default */}
      <DocumentHeader
        metaDescription={description}
        socialDescription={description}
        imgPath={null}
      />
      <Article
        headline={guide.name}
        image={company?.image_url ? [`${company.image_url}?w=200&h=200`] : undefined}
        datePublished={guide.createdAt}
        dateModified={guide.updatedAt}
      />

      {/* Main content */}
      <div className={`grid grid-cols-4 mx-auto pb-20 pt-0 md:pt-4${preview ? ' max-w-5xl' : ''}`}>

        {/* Body */}
        <div className="px-4 mt-2 md:mt-6 col-span-full md:col-span-3" ref={bodyRef}>
          {/* Mobile table of contents */}
          {tocSections?.length > 0 && (
            <div className="md:hidden mb-4">
              <TableOfContents
                sections={tocSections}
                mobile
                fallbackTargetId={!hasAccess ? 'guide-upgrade-cta' : undefined}
              />
            </div>
          )}

          {guide.author && (
            <AuthorCard author={guide.author} className="mb-4" minimal />
          )}

          {guide.video && (
            <div className="rounded overflow-hidden max-w-5xl mx-auto mt-0 mb-4">
              <YouTubeVideo
                videoID={guide.video}
                onView={() => tracker.guideViewVideo({guide: guide.name})}
                onPlay={() => tracker.guidePlayVideo({guide: guide.name})}
              />
            </div>
          )}

          {/* New guide content format */}
          {renderedContent && (
            hasAccess ? (
              <GuideCompanyContext.Provider value={guide.companyTag || null}>
                <QuestionsBatchProvider>
                  <GuideMDX onRender={() => setTOC(generateTOC(bodyRef.current))}>
                    {renderedContent}
                  </GuideMDX>
                </QuestionsBatchProvider>
              </GuideCompanyContext.Provider>
            ) : (
              <GuideCompanyContext.Provider value={guide.companyTag || null}>
                <QuestionsBatchProvider>
                  <LockedSection
                    lockedContent={lockedPreviewContent}
                    loginHref={loginHref}
                    onClickUpgrade={() => tracker.guideClickUpgradeCta({guide: guide.name})}
                  >
                    <GuideMDX onRender={() => setTOC(generateTOC(bodyRef.current))}>
                      {renderedContent}
                    </GuideMDX>
                  </LockedSection>
                </QuestionsBatchProvider>
              </GuideCompanyContext.Provider>
            )
          )}

          {showResumesPreview && <GuideResumesPreview guide={guide} companyName={company?.name} />}

          {/* FAQ Section — only shown to users with access */}
          {hasAccess && guide.faq?.length > 0 && (
            <Section title="FAQ">
              <FAQSection items={guide.faq} includeMetadata />
            </Section>
          )}
        </div>

        {/* Sticky sidebar */}
        <div className="hidden md:block col-span-1">
          <div className="sticky top-20">
            {tocSections?.length > 0 && (
              <div className="hidden md:block">
                <TableOfContents
                  sections={tocSections}
                  hideHeader
                  className="max-h-80"
                  fallbackTargetId={!hasAccess ? 'guide-upgrade-cta' : undefined}
                />
              </div>
            )}

            {tocReady && (
              <div className="px-2">
                <GuideSidebarCard guide={guide} tracker={tracker} />
              </div>
            )}
          </div>
        </div>

        {!currentUser && (
          <div className="col-span-full flex justify-center">
            <div className="container">
              <SignupBox
                role={guide.roleName}
                href="/signup?src=guide"
                onClick={() => tracker.guideClickSignupCta({guide: guide.name, tag: guide.roleTag})}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidePage;
